import {
  useQuery,
  useQueryClient,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  getImagingById,
  getImagingsFiltered,
  getImagingTrackingNotifications,
  getPrefixDicom,
} from "@/apis";
import { http } from "@/apis/config/http";
import type {
  Imaging,
  ImagingsFilters,
  ImagingTrackingNotification,
} from "@/features/imagings/schemas/imagings";
import type { User } from "@/features/users/schemas/user";
import { searchUsersApi } from "@/features/users/services/userService";

// Query keys for imaging-related queries
export const imagingKeys = {
  all: ["imagings"] as const,
  filtered: (filters: {
    institutionId?: string;
    physicianId?: string;
    patientId?: string;
    accessionNumber?: string;
    startDate?: string;
    endDate?: string;
  }) => ["imagings", "filtered", filters] as const,
  detail: (id: string) => ["imagings", "detail", id] as const,
  users: {
    all: ["users"] as const,
    filtered: (search: string) => ["users", "filtered", { search }] as const,
  },
  trackingNotifications: (imagingIuid: string) =>
    ["imagings", "trackingNotifications", imagingIuid] as const,
  query: (userId?: string, params?: any) => ["imaging-query", userId, params] as const,
  medicalReports: (imagingId?: string) => ["medicalReports", imagingId] as const,
} as const;

const retryDelay = (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000);

/** Respuesta del endpoint GET /api/v1/imaging/query */
export interface ImagingQueryResult {
  data: ImagingStudy[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

/** Estudio de imagenología retornado por /api/v1/imaging/query */
export interface ImagingStudy {
  id: string;
  patient_name: string;
  patient_id: string;
  patient_gender?: string;
  control?: {
    created_at?: number;
  };
  order?: string;
  accession_number?: string;
  referring_physician?: string;
  institution_name?: string;
  modality?: string;
  date_study?: string;
  date_only?: string;
  upload_date?: string;
  number_imagings?: number;
  url_thubnail?: string;
  iuid?: string;
  description?: string;
}

/**
 * Hook para obtener instituciones activas con imagenología activa.
 * Usa la API Typesense (type=institution, includeUserData=true).
 * El filtro active_imaging se aplica en cliente.
 */
export function useFilteredUsers(search: string): UseQueryResult<User[], Error> {
  return useQuery<User[], Error>({
    queryKey: imagingKeys.users.filtered(search),
    queryFn: async () => {
      const result = await searchUsersApi({
        q: search,
        // Eliminamos el type estricto para que Typesense devuelva ambos
        includeUserData: true,
        hitsPerPage: 50,
        page: 1,
        active_imaging: true,
      });
      // Filtrar en cliente las instituciones y médicos con active_imaging activo
      return (result.data as unknown as User[]).filter(
        (u) =>
          (u.type === "institution" || u.type === "physician") &&
          (u as User & { active_imaging?: boolean }).active_imaging === true
      );
    },
    enabled: search.length >= 3,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay,
  });
}

/**
 * Hook para obtener imágenes filtradas
 */
export function useFilteredImagings(
  filters: ImagingsFilters,
  {
    enabled = true,
    ...options
  }: Omit<UseQueryOptions<Imaging[], Error>, "queryKey" | "queryFn">
): UseQueryResult<Imaging[], Error> {
  return useQuery({
    ...options,
    queryKey: imagingKeys.filtered(filters),
    queryFn: () => getImagingsFiltered(filters),
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes for imaging data
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay,
  });
}

/**
 * Hook para obtener una imagen específica por ID
 * @param {string} imagingId - El ID de la imagen
 * @returns {UseQueryResult<Imaging, Error>} La imagen obtenida
 */
export function useImagingDetail(
  imagingId?: string,
  refresh?: string
): UseQueryResult<Imaging, Error> {
  return useQuery({
    queryKey: [...imagingKeys.detail(imagingId ?? ""), refresh],
    queryFn: () => getImagingById(imagingId ?? ""),
    enabled: !!imagingId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay,
  });
}

export function useImagingTrackingNotifications(
  imagingIuid?: string
): UseQueryResult<ImagingTrackingNotification[], Error> {
  return useQuery({
    queryKey: imagingKeys.trackingNotifications(imagingIuid ?? ""),
    queryFn: () => getImagingTrackingNotifications(imagingIuid ?? ""),
    enabled: !!imagingIuid,
  });
}

/**
 * Hook para invalidar queries de imágenes
 */
export function useInvalidateImagingQueries() {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => {
      queryClient.invalidateQueries({ queryKey: imagingKeys.all });
      queryClient.invalidateQueries({ queryKey: ["imaging-query"] });
      queryClient.invalidateQueries({ queryKey: ["medicalReports"] });
    },
    invalidateFiltered: (filters: Parameters<typeof imagingKeys.filtered>[0]) =>
      queryClient.invalidateQueries({
        queryKey: imagingKeys.filtered(filters),
      }),
    invalidateDetail: (id: string) =>
      queryClient.invalidateQueries({ queryKey: imagingKeys.detail(id) }),
    invalidateTrackingNotifications: (imagingIuid: string) =>
      queryClient.invalidateQueries({
        queryKey: imagingKeys.trackingNotifications(imagingIuid),
      }),
    invalidateUserQuery: (userId?: string) =>
      queryClient.invalidateQueries({ queryKey: ["imaging-query", userId] }),
    removeAll: () => {
      queryClient.removeQueries({ queryKey: imagingKeys.all });
      queryClient.removeQueries({ queryKey: ["imaging-query"] });
      queryClient.removeQueries({ queryKey: ["medicalReports"] });
    },
  };
}

/**
 * Hook para obtener el prefijo DICOM de un usuario
 * @param userId - El ID del usuario
 * @returns {UseQueryResult<string, Error>} El prefijo DICOM
 */
export function useUserPrefixDicom(userId?: string): UseQueryResult<string, Error> {
  return useQuery({
    queryKey: ["user-prefix-dicom", userId],
    queryFn: () => getPrefixDicom(userId!),
    enabled: !!userId,
    staleTime: Infinity, // El prefijo no suele cambiar
  });
}
/**
 * Hook para consultar los estudios de imagenología de un usuario.
 * Endpoint: GET /api/v1/imaging/query?user_id=:userId
 */
export function useUserImagingQuery(
  userId: string | undefined,
  params?: {
    skip?: number;
    limit?: number;
    q?: string;
    modality?: string;
    date_study?: string;
    refresh?: string;
  }
): UseQueryResult<ImagingQueryResult, Error> {
  return useQuery<ImagingQueryResult, Error>({
    queryKey: ["imaging-query", userId, params],
    queryFn: () =>
      http.get<ImagingQueryResult>("/api/v1/imaging/query", {
        user_id: userId!,
        type_query: "personal",
        limit: params?.limit ?? 20,
        ...(params?.skip !== undefined && { skip: params.skip }),
        ...(params?.q && { q: params.q }),
        ...(params?.modality && { modality: params.modality }),
        ...(params?.date_study && { date_study: params.date_study }),
        ...(params?.refresh && { refresh: params.refresh }),
      }),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
    retry: 2,
    retryDelay,
  });
}
